"use client";

import {
	RiHeartFill,
	RiQuillPenAiLine,
} from "@remixicon/react";
import type { DbShowReview } from "@workspace/types";
import {
	Avatar,
	AvatarFallback,
	AvatarImage,
} from "@workspace/ui/components/avatar";
import { Button } from "@workspace/ui/components/button";
import { Card, CardContent } from "@workspace/ui/components/card";
import { cn } from "@workspace/ui/lib/utils";
import Link from "next/link";
import { StatusBadge } from "@/components/shared/status-badge";
import { memo } from "react";
import { useCurrentUserDisplay } from "@/hooks/shared/use-auth";
import { hasReviewData } from "@workspace/shared-utils";
import { renderStarRating } from "@/lib/utils/set-card-helpers";

interface YourShowReviewCardProps {
	existingReview?: DbShowReview | null;
	onOpenModal: () => void;
}

export const YourShowReviewCard = memo<YourShowReviewCardProps>(
	function YourShowReviewCard({ existingReview, onOpenModal }) {
		const { data: currentUser } = useCurrentUserDisplay();

		// Don't render if user is not logged in
		if (!currentUser) {
			return null;
		}

		// If no review exists at all, show a "Write Review" state
		if (!existingReview) {
			return (
				<Card className="border bg-card shadow-sm">
					<CardContent className="p-6 text-center">
						<div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
							<RiQuillPenAiLine className="h-6 w-6 text-muted-foreground" />
						</div>
						<p className="mb-4 text-muted-foreground">
							You haven't reviewed this show yet.
						</p>
						<Button onClick={onOpenModal} size="sm">
							Write Review
						</Button>
					</CardContent>
				</Card>
			);
		}

		// Use unified function to check if review has meaningful data
		const reviewHasData = hasReviewData(existingReview);

		// If review exists but has no data, show "Write Review" state
		if (!reviewHasData) {
			return (
				<Card className="border bg-card shadow-sm">
					<CardContent className="p-6 text-center">
						<div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
							<RiQuillPenAiLine className="h-6 w-6 text-muted-foreground" />
						</div>
						<p className="mb-4 text-muted-foreground">
							You haven't reviewed this show yet.
						</p>
						<Button onClick={onOpenModal} size="sm">
							Write Review
						</Button>
					</CardContent>
				</Card>
			);
		}

		return (
			<Card
				className={cn(
					"relative w-full cursor-pointer overflow-hidden border bg-card shadow-sm transition-all duration-150",
					"hover:border-primary/20 hover:shadow-md",
					"active:scale-[0.99]",
				)}
				onClick={onOpenModal}
				role="button"
				tabIndex={0}
				onKeyDown={(e) => {
					if (e.key === "Enter" || e.key === " ") {
						e.preventDefault();
						onOpenModal();
					}
				}}
			>
				<CardContent className="p-3">
					<div className="flex gap-3">
						{/* Left: Profile pic */}
						<div className="flex-shrink-0">
							{currentUser.username ? (
								<Link
									href={`/${currentUser.username}`}
									className="group block rounded-full outline-none focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:ring-offset-2"
									aria-label={`View ${currentUser.username} profile`}
									onClick={(e) => e.stopPropagation()} // Prevent card click when clicking avatar
								>
									<div className="overflow-hidden rounded-full transition-all duration-200 group-hover:ring-4 group-hover:ring-primary/10 group-active:scale-95 group-active:ring-primary/20">
										<div className="transition-transform duration-200 group-hover:scale-105 group-active:scale-100">
											<Avatar className="h-12 w-12">
												<AvatarImage
													src={currentUser.avatar || undefined}
													alt={currentUser.username || currentUser.name}
												/>
												<AvatarFallback className="text-sm">
													{(currentUser.username || currentUser.name)
														.charAt(0)
														.toUpperCase()}
												</AvatarFallback>
											</Avatar>
										</div>
									</div>
								</Link>
							) : (
								<Avatar className="h-12 w-12">
									<AvatarImage
										src={currentUser.avatar || undefined}
										alt={currentUser.username || currentUser.name}
									/>
									<AvatarFallback className="text-sm">
										{(currentUser.username || currentUser.name)
											.charAt(0)
											.toUpperCase()}
									</AvatarFallback>
								</Avatar>
							)}
						</div>

						{/* Middle: Username and Stars column */}
						<div className="flex flex-col justify-start gap-1">
							{/* Username on top */}
							<p className="truncate font-medium text-sm">
								{currentUser.username || currentUser.name}
							</p>

							{/* Star rating and heart below username */}
							{(existingReview.rating > 0 || existingReview.loved) && (
								<div className="-ml-0.5 flex items-center gap-1">
									{existingReview.rating > 0 && (
										<div className="flex items-center">
											{renderStarRating({
												rating: existingReview.rating,
												size: "lg",
											})}
										</div>
									)}
									{existingReview.loved && (
										<RiHeartFill className="h-5 w-5 text-red-500" />
									)}
								</div>
							)}
						</div>

						{/* Right: Status badges in top right */}
						<div className="ml-auto flex flex-col gap-1">
							{existingReview.must_see && (
								<StatusBadge status="mustSee" className="justify-center" />
							)}
							{existingReview.want_to_see && !existingReview.must_see && (
								<StatusBadge status="wantToSee" className="justify-center" />
							)}
							{existingReview.saw_live ? (
								<StatusBadge status="sawLive" className="justify-center" />
							) : existingReview.saw_stream ? (
								<StatusBadge status="sawStream" className="justify-center" />
							) : null}
						</div>
					</div>

					{/* Review text content below - full width */}
					{existingReview.content && (
						<div className="mt-3">
							<p className="text-left text-sm">{existingReview.content}</p>
						</div>
					)}
				</CardContent>
			</Card>
		);
	},
);
